import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { MANAGED_INVENTORY_ROUTES } from 'routes/constants';
import { AppBar, Toolbar } from '@mui/material';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { useGetStockLocationQuery } from 'store/slices/apiV1/inventory';
import {
  IconPlaneDeparture,
  IconTransferIn,
  IconDashboard,
  IconBasket,
  IconListCheck,
  IconTruckDelivery,
  IconTallymarks,
} from '@tabler/icons-react';
import { useDeviceContext } from 'hooks/useDeviceContext';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import useLocalStorage from 'hooks/useLocalStorage';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { ALL_APPS } from 'constants/appConstants';
import {
  WorkstationType,
  workstationTypeSpecifications,
} from 'types/clientV2/stockLocations';
import _ from 'lodash';
import { useEffect } from 'react';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';

const BottomAppBar = () => {
  const tempKitDashboard = useFeature(FeatureFlags.tempKitDashboard).on;
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { desktopScreen } = useDeviceContext();
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);
  const { data: facility } = useGetStockLocationQuery(
    settings.facility || skipToken
  );

  const workstationSpecs = _.get(
    workstationTypeSpecifications,
    settings?.workstationType
  );

  const hideNavigation = workstationSpecs?.routes.length < 2;

  useEffect(() => {
    if (
      pathname === MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD &&
      !tempKitDashboard
    ) {
      navigate(MANAGED_INVENTORY_ROUTES.DASHBOARD);
    }
    if (workstationSpecs?.routes.length === 1) {
      navigate(workstationSpecs?.routes[0]);
    }
  }, []);

  return (
    <AppBar
      position="sticky"
      color="primary"
      sx={{
        top: 'auto',
        bottom: 0,
      }}
    >
      {!hideNavigation && (
        <Toolbar>
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.DASHBOARD
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.DASHBOARD
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.DASHBOARD)}
                startIcon={
                  <IconDashboard
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.DASHBOARD
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                Dashboard
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.DASHBOARD
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD)}
                startIcon={
                  <IconDashboard
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                Kit Dashboard
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.DASHBOARD
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.DASHBOARD)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconDashboard
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.DASHBOARD
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.COUNTER
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.COUNTER
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.COUNTER)}
                startIcon={
                  <IconTallymarks
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.COUNTER
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                Counter
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.COUNTER
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.COUNTER)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTallymarks
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.COUNTER
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.TRANSFER
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.TRANSFER
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.TRANSFER)}
                startIcon={
                  <IconTransferIn
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.TRANSFER
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Move
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.TRANSFER
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.TRANSFER)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTransferIn
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.TRANSFER
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.RECEIVE
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.RECEIVE
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.RECEIVE)}
                startIcon={
                  <IconTruckDelivery
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.RECEIVE
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Receive
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.RECEIVE
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.RECEIVE)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTruckDelivery
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.RECEIVE
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.PRE_RECEIVE
          ) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.PRE_RECEIVE
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.PRE_RECEIVE)}
                startIcon={
                  <IconTruckDelivery
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.PRE_RECEIVE
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Pre Receive
              </SegmentButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.PRE_RECEIVE
          ) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.PRE_RECEIVE)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTruckDelivery
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.PRE_RECEIVE
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE
          ) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE)}
                startIcon={
                  <IconTruckDelivery
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Fixed Receive
              </SegmentButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE
          ) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTruckDelivery
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE
          ) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() =>
                  navigate(MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE)
                }
                startIcon={
                  <IconTruckDelivery
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Inline Receive
              </SegmentButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE
          ) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() =>
                  navigate(MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE)
                }
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTruckDelivery
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE
          ) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() =>
                  navigate(MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE)
                }
                startIcon={
                  <IconTruckDelivery
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Manual Receive
              </SegmentButton>
            )}
          {_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE
          ) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() =>
                  navigate(MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE)
                }
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconTruckDelivery
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.PICKING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.PICKING
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.PICKING)}
                startIcon={
                  <IconBasket
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.PICKING
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Pick
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.PICKING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.PICKING)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconBasket
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.PICKING
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.KITTING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen &&
            facility?.warehouseHandlingEnabled && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.KITTING
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.KITTING)}
                startIcon={
                  <IconListCheck
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.KITTING
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Kitting
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.KITTING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen &&
            facility?.warehouseHandlingEnabled && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.KITTING)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconListCheck
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.KITTING
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}

          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.SHIPPING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            desktopScreen && (
              <SegmentButton
                size="large"
                color={
                  pathname === MANAGED_INVENTORY_ROUTES.SHIPPING
                    ? 'success'
                    : 'inherit'
                }
                sx={{ mr: 2 }}
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SHIPPING)}
                startIcon={
                  <IconPlaneDeparture
                    size={32}
                    color={
                      pathname === MANAGED_INVENTORY_ROUTES.SHIPPING
                        ? theme.palette.success.main
                        : 'white'
                    }
                  />
                }
                permissionScope={{
                  app: ALL_APPS.WAREHOUSE.id,
                }}
              >
                Shipping
              </SegmentButton>
            )}
          {(_.includes(
            workstationSpecs?.routes,
            MANAGED_INVENTORY_ROUTES.SHIPPING
          ) ||
            settings.workstationType === WorkstationType.LEGACY) &&
            !desktopScreen && (
              <SegmentIconButton
                color="inherit"
                onClick={() => navigate(MANAGED_INVENTORY_ROUTES.SHIPPING)}
                permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
              >
                <IconPlaneDeparture
                  color={
                    pathname === MANAGED_INVENTORY_ROUTES.SHIPPING
                      ? theme.palette.success.main
                      : 'white'
                  }
                />
              </SegmentIconButton>
            )}
        </Toolbar>
      )}
    </AppBar>
  );
};

export default BottomAppBar;
